import { getPracticeAndHours } from "@frontend/lib/actions/actions";
import type { GetPracticeAndHoursQuery } from "@frontend/lib/graphql/generated-gql";
import { useTranslations } from "@frontend/lib/next-intl-typed";
import { useBoundStore } from "@frontend/lib/stores/globalStore";
import { SelectedAppointmentTimeEnum } from "@frontend/lib/stores/slices/bookingSlice";
import { Button } from "@frontend/shared/components/ui/button";
import { Calendar } from "@frontend/shared/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@frontend/shared/components/ui/popover";
import { useQuery } from "@tanstack/react-query";
import { addDays, addYears, differenceInHours, isAfter } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { CalendarPlus, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useMediaQuery } from "usehooks-ts";
import { useBooking } from "../context/BookingContext";
import {
  getNext5Days,
  getStartOfTodayInTimezone,
  timeStringToDate,
} from "../utils";
import { DateStrip } from "./DateStrip";
import PracticeHoursSlots from "./PracticeHoursSlots";

export const AppointmentDateTimeSelector = ({
  primaryColor,
  secondaryBackgroundColor,
  experityBooking,
  handleSkipBooking,
}: {
  primaryColor: string;
  secondaryBackgroundColor: string;
  experityBooking: boolean;
  handleSkipBooking: (
    overrideAppointmentTime?: Date | SelectedAppointmentTimeEnum,
  ) => void;
}) => {
  const t = useTranslations("Shop");
  const {
    selectedAppointmentTime,
    setSelectedAppointmentTime,
    currentDisplayDates,
    setCurrentDisplayDates,
    setSelectedDay,
  } = useBooking();
  const selectedPracticeId = useBoundStore.use.practiceId();
  const isMobile = useMediaQuery("(max-width: 1024px)");

  const { data: practiceHours } = useQuery<
    GetPracticeAndHoursQuery,
    Error,
    GetPracticeAndHoursQuery["practice"]
  >({
    queryKey: ["practiceHoursSlots", selectedPracticeId],
    enabled: selectedPracticeId != null,
    select: (data) => {
      return data.practice;
    },
    queryFn: () => {
      if (selectedPracticeId === null) {
        throw new Error("Practice ID is required");
      }
      return getPracticeAndHours(selectedPracticeId);
    },
  });

  const [pageTransitionLoading, setPageTransitionLoading] =
    useState<boolean>(false);
  useEffect(() => {
    if (!practiceHours?.timezone) return;

    const tzStartOfToday = getStartOfTodayInTimezone(practiceHours.timezone);
    const tzStartOfDayAfterTomorrow = addDays(tzStartOfToday, 2);

    // On mobile, always start from today for better UX
    // On desktop, start from 2 days from now (unless experity booking)
    const startDate =
      isMobile || experityBooking ? tzStartOfToday : tzStartOfDayAfterTomorrow;
    const displayDates = getNext5Days(startDate);

    setCurrentDisplayDates(displayDates);
    // Set the first date as selected on mobile to match displayed slots
    if (isMobile) {
      setSelectedDay(displayDates[0]);
    }
  }, [
    practiceHours?.timezone,
    isMobile,
    setCurrentDisplayDates,
    setSelectedDay,
    experityBooking,
  ]);

  if (!practiceHours?.timezone) {
    return null;
  }

  const startOfToday = getStartOfTodayInTimezone(practiceHours.timezone);
  const startOfDayAfterTomorrow = addDays(startOfToday, 2);

  const todayDayOfWeek = formatInTimeZone(
    new Date(),
    practiceHours.timezone,
    "EEEE",
  ).toUpperCase();
  const practiceHoursForToday = practiceHours.practiceHours.find(
    (day) => day.dayOfWeek === todayDayOfWeek,
  );

  // Check if practice is open today and if there's at least 1 hour remaining before closing
  const now = new Date();
  const canShowASAP =
    practiceHoursForToday?.closeTime &&
    (() => {
      const closingTime = timeStringToDate(
        now,
        practiceHoursForToday.closeTime,
        practiceHours.timezone,
      );
      const hoursRemaining = differenceInHours(closingTime, now);
      // Show ASAP if there's at least 1 hour remaining before closing
      return hoursRemaining >= 1 && !isAfter(now, closingTime);
    })();

  return (
    <div className="w-full flex flex-col items-start gap-4 sm:px-2 lg:px-0">
      <p className="font-medium text-lg">{t("ChooseAnAppointmentTime")}</p>
      {canShowASAP && (
        <div
          style={{
            backgroundColor: secondaryBackgroundColor ?? "#FAFAFA",
          }}
          className="w-full flex lg:flex-row flex-col items-center justify-between gap-4 px-6 py-3 rounded-2xl"
        >
          <div className="flex flex-row items-start gap-1 w-full">
            <p className="font-bold lg:text-md text-sm">{t("ComingNow?")}</p>
            <p className="lg:text-md text-sm">{t("ChooseASAPandHeadOver")}</p>
          </div>
          <Button
            variant="outline"
            className="lg:w-fit w-full px-6 py-4 rounded-xl"
            onClick={() => {
              setPageTransitionLoading(true); // state resets if they go back to the page
              setSelectedAppointmentTime(SelectedAppointmentTimeEnum.ASAP);
              // Pass ASAP explicitly: the state update above won't be reflected in
              // handleSkipBooking's render-bound value within this same handler.
              handleSkipBooking(SelectedAppointmentTimeEnum.ASAP); // skips to next page right away
            }}
            style={{
              backgroundColor:
                selectedAppointmentTime === SelectedAppointmentTimeEnum.ASAP
                  ? primaryColor
                  : "white",
              color:
                selectedAppointmentTime === SelectedAppointmentTimeEnum.ASAP
                  ? "white"
                  : "black",
            }}
          >
            {pageTransitionLoading ? (
              <Loader2 className="animate-spin" size={16} />
            ) : (
              t("ASAP")
            )}
          </Button>
        </div>
      )}

      <div className="w-full h-full flex flex-col  p-6 bg-white border border-[#E1E1E1] rounded-2xl lg:min-h-[400px] sm:min-h-[400px]">
        <div className="w-full flex flex-row items-center justify-between h-fit">
          <p className="text-md">
            {currentDisplayDates[0]
              ? formatInTimeZone(
                  currentDisplayDates[0],
                  practiceHours.timezone,
                  "MMM yyyy",
                )
              : formatInTimeZone(
                  new Date(),
                  practiceHours.timezone,
                  "MMM yyyy",
                )}
            {" - "}
            {practiceHours.timezone}
          </p>
          <Popover>
            <PopoverTrigger
              className="w-fit rounded-xl flex flex-row items-center gap-2 border px-4 py-2"
              style={{
                color: primaryColor ?? "#000000",
                borderColor: primaryColor ?? "#000000",
              }}
            >
              <CalendarPlus color={primaryColor ?? "#000000"} size={14} />
              <p className="text-xs font-normal">{t("JumpToDate")}</p>
            </PopoverTrigger>
            <PopoverContent className="p-0 border-0">
              <Calendar
                mode="single"
                selected={currentDisplayDates[0]}
                onSelect={(newValue) => {
                  if (newValue) {
                    setCurrentDisplayDates(getNext5Days(newValue));
                  }
                }}
                className="border rounded-md w-full"
                showOutsideDays={false}
                captionLayout="dropdown"
                fromDate={startOfToday}
                toDate={addYears(new Date(), 1)}
              />
            </PopoverContent>
          </Popover>
        </div>
        <div className="">
          <DateStrip
            className="lg:w-full mt-4"
            getNext5Days={getNext5Days}
            showMonthAndYear={false}
            primaryColor={primaryColor}
            experityBooking={experityBooking}
            timezone={practiceHours.timezone}
          />
          <div className="mt-4 w-full">
            <PracticeHoursSlots
              primaryColor={primaryColor}
              noAvailableSlotsLeftToday={!canShowASAP}
              practiceHoursSlots={practiceHours}
              timezone={practiceHours.timezone}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppointmentDateTimeSelector;
